import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { CurrencyInr, ArrowCircleUp, ArrowCircleDown, ReceiptX, Storefront, Wallet } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * FIAT — parallel zero-GST cash business.
 *
 * All ledgers touched here get a "— Fiat" suffix; money can only flow through
 * the FIAT BANK ledger. Compliance side (GST/TDS/RCM) is completely off the
 * grid. Everything still shows up in the combined P&L via its own column.
 */
export default function Fiat() {
    const { user } = useAuth();
    const canPurchase = ["founder", "accountant", "purchase"].includes(user?.role);
    const canSale     = ["founder", "accountant", "sales_staff", "dispatch"].includes(user?.role);
    const canReceive  = ["founder", "accountant", "sales_staff", "dispatch"].includes(user?.role);
    const canPay      = ["founder", "accountant", "purchase"].includes(user?.role);
    const canExpense  = ["founder", "accountant"].includes(user?.role);

    // Default the active tab to the first thing the user is allowed to do
    const defaultTab = canSale ? "invoice" : canPurchase ? "bill" : canReceive ? "receipt" : "payment";
    const [tab, setTab] = useState(defaultTab);

    // History lists
    const [bills, setBills] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [balances, setBalances] = useState({ customers: [], vendors: [] });

    const load = async () => {
        try {
            const calls = [];
            if (canPurchase) calls.push(api.get("/fiat/bills").then((r) => setBills(r.data)));
            if (canSale)     calls.push(api.get("/fiat/invoices").then((r) => setInvoices(r.data)));
            if (canReceive)  calls.push(api.get("/fiat/receipts").then((r) => setReceipts(r.data)));
            if (canPay)      calls.push(api.get("/fiat/payments").then((r) => setPayments(r.data)));
            calls.push(api.get("/fiat/parties/balances").then((r) => setBalances(r.data)));
            await Promise.all(calls);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const tabs = useMemo(() => ([
        canSale     && { key: "invoice", label: "Sale invoice",  icon: <Storefront size={14} />, hint: "Money owed by customer" },
        canPurchase && { key: "bill",    label: "Purchase bill", icon: <ReceiptX size={14} />, hint: "Amount owed to vendor" },
        canReceive  && { key: "receipt", label: "Receipt (in)",  icon: <ArrowCircleDown size={14} />, hint: "Money into FIAT BANK" },
        canPay      && { key: "payment", label: "Payment (out)", icon: <ArrowCircleUp size={14} />, hint: "Money out of FIAT BANK" },
    ].filter(Boolean)), [canPurchase, canSale, canReceive, canPay]);

    return (
        <>
            <Toaster richColors />
            <PageHeader
                title="Fiat vouchers"
                sub="Parallel zero-GST cash business — all activity flows through FIAT BANK. Off the GST/TDS grid, still visible in the combined P&L."
                right={<span className="text-[11px] uppercase tracking-widest text-stone-500">Ledgers suffixed <span className="font-mono">— Fiat</span></span>}
            />

            <div className="p-8 space-y-6">
                <PartyBalances balances={balances} />

                <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                    <div className="p-3 grid gap-2"
                         style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
                         data-testid="fiat-tabs">
                        {tabs.map((t) => {
                            const active = tab === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    className={`text-left rounded-lg border px-4 py-3 transition-all
                                        ${active
                                            ? "shadow-[0_4px_18px_-6px_rgba(244,63,94,0.35)]"
                                            : "hover:border-stone-300 hover:bg-stone-50"}`}
                                    style={{
                                        borderColor: active ? "var(--se-brand)" : "var(--se-border)",
                                        background: active ? "var(--se-brand-grad-soft)" : "#fff",
                                    }}
                                    data-testid={`fiat-tab-${t.key}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${active ? "text-white" : "text-stone-500"}`}
                                              style={{ background: active ? "var(--se-brand-grad)" : "#f5f5f4" }}>
                                            {t.icon}
                                        </span>
                                        <span className={`text-[15px] font-semibold ${active ? "text-stone-900" : "text-stone-800"}`}>
                                            {t.label}
                                        </span>
                                    </div>
                                    <div className={`text-[11px] mt-1.5 ${active ? "text-stone-700" : "text-stone-500"}`}>
                                        {t.hint}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="p-6 border-t" style={{ borderColor: "var(--se-border)" }}>
                        {tab === "bill"     && <FiatBillForm onDone={load} />}
                        {tab === "invoice"  && <FiatInvoiceForm onDone={load} />}
                        {tab === "receipt"  && <FiatReceiptForm balances={balances} onDone={load} />}
                        {tab === "payment"  && <FiatPaymentForm balances={balances} canExpense={canExpense} onDone={load} />}
                    </div>
                </div>

                <HistoryPanels
                    tab={tab}
                    bills={bills}
                    invoices={invoices}
                    receipts={receipts}
                    payments={payments}
                    onReversed={load}
                />
            </div>
        </>
    );
}


function PartyBalances({ balances }) {
    const customers = (balances.customers || []).filter((c) => Math.abs(c.balance) > 0.5);
    const vendors   = (balances.vendors   || []).filter((v) => Math.abs(v.balance) > 0.5);
    if (customers.length === 0 && vendors.length === 0) return null;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="fiat-party-balances">
            <BalancePanel
                title="Customer receivables — Fiat"
                rows={customers}
                positive="owes us"
                negative="advance sitting"
                testid="fiat-balance-customers"
            />
            <BalancePanel
                title="Vendor payables — Fiat"
                rows={vendors}
                positive="we owe"
                negative="advance paid"
                testid="fiat-balance-vendors"
            />
        </div>
    );
}

function BalancePanel({ title, rows, positive, negative, testid }) {
    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid={testid}>
            <div className="px-4 py-2 border-b font-serif text-sm" style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                {title}
            </div>
            <table className="se-table">
                <thead><tr><th>Party</th><th className="text-right">Balance</th><th></th></tr></thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan="3" className="text-center text-stone-500 py-3 text-xs">All settled.</td></tr>
                    ) : rows.map((r) => (
                        <tr key={r.ledger_id} data-testid={`balance-row-${r.ledger_id}`}>
                            <td>{r.party_name}</td>
                            <td className="se-num" style={{ color: r.balance > 0 ? "#9f1239" : "#0f766e" }}>
                                ₹ {inr(Math.abs(r.balance))}
                            </td>
                            <td className="text-xs text-stone-500">{r.balance > 0 ? positive : negative}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


// -----------------------------------------------------------------------------
// Forms — one per entity. Kept intentionally small: only the fields the
// journal actually needs.
// -----------------------------------------------------------------------------
const today = () => new Date().toISOString().slice(0, 10);

function FiatInvoiceForm({ onDone }) {
    const [form, setForm] = useState({ date: today(), customer_name: "", amount: "", invoice_no: "", description: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const amt = Number(form.amount);
        if (!form.customer_name.trim()) return toast.error("Customer name is required");
        if (!amt || amt <= 0) return toast.error("Amount must be positive");
        setBusy(true);
        try {
            await api.post("/fiat/invoices", {
                date: form.date, customer_name: form.customer_name.trim(),
                amount: amt,
                invoice_no: form.invoice_no.trim() || null,
                description: form.description.trim() || null,
            });
            toast.success(`Fiat sale invoice ₹${inr(amt)} — Dr ${form.customer_name.trim()} — Fiat`);
            setForm({ date: today(), customer_name: "", amount: "", invoice_no: "", description: "" });
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="grid md:grid-cols-6 gap-3 items-end" data-testid="fiat-invoice-form">
            <Field label="Date" testid="invoice-date">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" />
            </Field>
            <Field label="Customer name" testid="invoice-customer" span={2}>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                       placeholder="Sajest" className="h-9" />
            </Field>
            <Field label="Amount ₹" testid="invoice-amount">
                <Input type="number" min="0" step="0.01" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 se-num text-right" />
            </Field>
            <Field label="Invoice no." testid="invoice-no">
                <Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} className="h-9 font-mono" />
            </Field>
            <div className="flex justify-end">
                <Button onClick={submit} disabled={busy} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="invoice-submit" className="h-9 w-full">
                    {busy ? "Posting…" : "Post invoice"}
                </Button>
            </div>
            <Field label="Description" testid="invoice-desc" span={6}>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="What was sold?" className="h-9" />
            </Field>
            <JournalHint span={6}
                dr={form.customer_name.trim() ? `${form.customer_name.trim()} — Fiat` : "<Customer> — Fiat"}
                cr="Sales — Fiat"
                amount={form.amount}
            />
        </div>
    );
}

function FiatBillForm({ onDone }) {
    const [form, setForm] = useState({ date: today(), vendor_name: "", amount: "", bill_no: "", description: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const amt = Number(form.amount);
        if (!form.vendor_name.trim()) return toast.error("Vendor name is required");
        if (!amt || amt <= 0) return toast.error("Amount must be positive");
        setBusy(true);
        try {
            await api.post("/fiat/bills", {
                date: form.date, vendor_name: form.vendor_name.trim(),
                amount: amt,
                bill_no: form.bill_no.trim() || null,
                description: form.description.trim() || null,
            });
            toast.success(`Fiat purchase bill ₹${inr(amt)} — Cr ${form.vendor_name.trim()} — Fiat`);
            setForm({ date: today(), vendor_name: "", amount: "", bill_no: "", description: "" });
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="grid md:grid-cols-6 gap-3 items-end" data-testid="fiat-bill-form">
            <Field label="Date" testid="bill-date">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" />
            </Field>
            <Field label="Vendor name" testid="bill-vendor" span={2}>
                <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                       placeholder="K Hardware" className="h-9" />
            </Field>
            <Field label="Amount ₹" testid="bill-amount">
                <Input type="number" min="0" step="0.01" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 se-num text-right" />
            </Field>
            <Field label="Bill no." testid="bill-no">
                <Input value={form.bill_no} onChange={(e) => setForm({ ...form, bill_no: e.target.value })} className="h-9 font-mono" />
            </Field>
            <div className="flex justify-end">
                <Button onClick={submit} disabled={busy} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="bill-submit" className="h-9 w-full">
                    {busy ? "Posting…" : "Post bill"}
                </Button>
            </div>
            <Field label="Description" testid="bill-desc" span={6}>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-9" />
            </Field>
            <JournalHint span={6}
                dr="Purchases — Fiat"
                cr={form.vendor_name.trim() ? `${form.vendor_name.trim()} — Fiat` : "<Vendor> — Fiat"}
                amount={form.amount}
            />
        </div>
    );
}

function FiatReceiptForm({ balances, onDone }) {
    const [form, setForm] = useState({ date: today(), customer_name: "", amount: "", ref: "", description: "" });
    const [busy, setBusy] = useState(false);
    const suggestions = (balances.customers || []).filter((c) => c.balance > 0.5);
    const submit = async () => {
        const amt = Number(form.amount);
        if (!amt || amt <= 0) return toast.error("Amount must be positive");
        setBusy(true);
        try {
            await api.post("/fiat/receipts", {
                date: form.date,
                customer_name: form.customer_name.trim() || null,
                amount: amt,
                ref: form.ref.trim() || null,
                description: form.description.trim() || null,
            });
            toast.success(`Fiat receipt ₹${inr(amt)} — Dr FIAT BANK`);
            setForm({ date: today(), customer_name: "", amount: "", ref: "", description: "" });
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const cr = form.customer_name.trim() ? `${form.customer_name.trim()} — Fiat` : "Sales — Fiat (generic)";
    return (
        <div className="grid md:grid-cols-6 gap-3 items-end" data-testid="fiat-receipt-form">
            <Field label="Date" testid="receipt-date">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" />
            </Field>
            <Field label="Customer (optional)" testid="receipt-customer" span={2}>
                <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                       placeholder="Sajest — leave blank if generic" className="h-9" list="fiat-cust-suggest" />
                <datalist id="fiat-cust-suggest">
                    {suggestions.map((s) => (<option key={s.ledger_id} value={s.party_name}>{`${s.party_name} — ₹${inr(s.balance)} owed`}</option>))}
                </datalist>
            </Field>
            <Field label="Amount ₹" testid="receipt-amount">
                <Input type="number" min="0" step="0.01" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 se-num text-right" />
            </Field>
            <Field label="Ref" testid="receipt-ref">
                <Input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} className="h-9 font-mono" />
            </Field>
            <div className="flex justify-end">
                <Button onClick={submit} disabled={busy} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="receipt-submit" className="h-9 w-full">
                    {busy ? "Posting…" : "Post receipt"}
                </Button>
            </div>
            <Field label="Description" testid="receipt-desc" span={6}>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-9" />
            </Field>
            <JournalHint span={6} dr="FIAT BANK" cr={cr} amount={form.amount} />
        </div>
    );
}

function FiatPaymentForm({ balances, canExpense, onDone }) {
    const [form, setForm] = useState({
        date: today(), kind: "vendor", vendor_name: "", expense_head: "",
        amount: "", ref: "", description: "",
    });
    const [busy, setBusy] = useState(false);
    const suggestions = (balances.vendors || []).filter((v) => v.balance > 0.5);
    const submit = async () => {
        const amt = Number(form.amount);
        if (!amt || amt <= 0) return toast.error("Amount must be positive");
        if (form.kind === "vendor" && !form.vendor_name.trim()) return toast.error("Vendor name is required");
        if (form.kind === "expense" && !form.expense_head.trim()) return toast.error("Expense head is required");
        setBusy(true);
        try {
            await api.post("/fiat/payments", {
                date: form.date,
                kind: form.kind,
                vendor_name: form.kind === "vendor" ? form.vendor_name.trim() : null,
                expense_head: form.kind === "expense" ? form.expense_head.trim() : null,
                amount: amt,
                ref: form.ref.trim() || null,
                description: form.description.trim() || null,
            });
            toast.success(`Fiat payment ₹${inr(amt)} — Cr FIAT BANK`);
            setForm({ date: today(), kind: "vendor", vendor_name: "", expense_head: "", amount: "", ref: "", description: "" });
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const dr = form.kind === "vendor"
        ? (form.vendor_name.trim() ? `${form.vendor_name.trim()} — Fiat` : "<Vendor> — Fiat")
        : (form.expense_head.trim() ? `${form.expense_head.trim()} — Fiat` : "<Expense head> — Fiat");
    return (
        <div className="grid md:grid-cols-6 gap-3 items-end" data-testid="fiat-payment-form">
            <Field label="Date" testid="payment-date">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" />
            </Field>
            <Field label="Kind" testid="payment-kind">
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="vendor">Vendor bill</SelectItem>
                        <SelectItem value="expense" disabled={!canExpense}>
                            Direct expense{!canExpense ? " (founder/accountant only)" : ""}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </Field>
            {form.kind === "vendor" ? (
                <Field label="Vendor name" testid="payment-vendor" span={2}>
                    <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                           placeholder="K Hardware" className="h-9" list="fiat-vend-suggest" />
                    <datalist id="fiat-vend-suggest">
                        {suggestions.map((s) => (<option key={s.ledger_id} value={s.party_name}>{`${s.party_name} — ₹${inr(s.balance)} owed`}</option>))}
                    </datalist>
                </Field>
            ) : (
                <Field label="Expense head" testid="payment-head" span={2}>
                    <Input value={form.expense_head} onChange={(e) => setForm({ ...form, expense_head: e.target.value })}
                           placeholder="Salaries, Rent, Miscellaneous…" className="h-9" />
                </Field>
            )}
            <Field label="Amount ₹" testid="payment-amount">
                <Input type="number" min="0" step="0.01" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 se-num text-right" />
            </Field>
            <Field label="Ref" testid="payment-ref">
                <Input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} className="h-9 font-mono" />
            </Field>
            <Field label="Description" testid="payment-desc" span={5}>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-9" />
            </Field>
            <div className="flex justify-end">
                <Button onClick={submit} disabled={busy} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="payment-submit" className="h-9 w-full">
                    {busy ? "Posting…" : "Post payment"}
                </Button>
            </div>
            <JournalHint span={6} dr={dr} cr="FIAT BANK" amount={form.amount} />
        </div>
    );
}


function Field({ label, children, span = 1, testid }) {
    return (
        <div style={{ gridColumn: `span ${span} / span ${span}` }} data-testid={testid}>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
            {children}
        </div>
    );
}

function JournalHint({ dr, cr, amount, span = 6 }) {
    const amt = Number(amount || 0);
    return (
        <div style={{ gridColumn: `span ${span} / span ${span}` }} className="rounded-md border p-3 text-xs flex items-center gap-4 flex-wrap"
             data-testid="journal-hint"
             {...({ "data-dr": dr, "data-cr": cr })}>
            <span className="text-[10px] uppercase tracking-widest text-stone-500">Journal preview</span>
            <span><span className="text-stone-500">Dr</span> <b>{dr}</b></span>
            <span><span className="text-stone-500">Cr</span> <b>{cr}</b></span>
            <span className="se-num">₹ {amt > 0 ? inr(amt) : "—"}</span>
        </div>
    );
}


// -----------------------------------------------------------------------------
// History
// -----------------------------------------------------------------------------
function HistoryPanels({ tab, bills, invoices, receipts, payments, onReversed }) {
    const rev = async (kind, id) => {
        const reason = window.prompt("Reason for reversal?");
        if (!reason?.trim()) return;
        try {
            await api.post(`/fiat/${kind}/${id}/reverse`, { reason: reason.trim() });
            toast.success("Reversed");
            onReversed?.();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    if (tab === "invoice") return <InvoiceHistory rows={invoices} onReverse={(id) => rev("invoice", id)} />;
    if (tab === "bill")    return <BillHistory rows={bills} onReverse={(id) => rev("bill", id)} />;
    if (tab === "receipt") return <ReceiptHistory rows={receipts} onReverse={(id) => rev("receipt", id)} />;
    if (tab === "payment") return <PaymentHistory rows={payments} onReverse={(id) => rev("payment", id)} />;
    return null;
}

function TableBase({ testid, title, cols, rows, renderRow }) {
    return (
        <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }} data-testid={testid}>
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                <div className="font-serif text-lg">{title}</div>
                <div className="text-xs text-stone-500">{rows.length} on record</div>
            </div>
            <table className="se-table">
                <thead><tr>{cols.map((c) => <th key={c.key} className={c.right ? "text-right" : ""}>{c.label}</th>)}</tr></thead>
                <tbody>
                    {rows.length === 0 ? <tr><td colSpan={cols.length} className="text-center text-stone-500 py-6 text-xs">No entries yet.</td></tr> :
                        rows.map(renderRow)}
                </tbody>
            </table>
        </div>
    );
}

function InvoiceHistory({ rows, onReverse }) {
    return (
        <TableBase testid="history-invoices" title="Fiat sale invoices"
            cols={[
                { key: "date", label: "Date" }, { key: "cust", label: "Customer" },
                { key: "no", label: "Inv no." }, { key: "desc", label: "Desc." },
                { key: "amt", label: "Amount", right: true }, { key: "act", label: "" },
            ]}
            rows={rows}
            renderRow={(r) => (
                <tr key={r.id} data-testid={`inv-row-${r.id}`} className={r.reversed ? "opacity-50" : ""}>
                    <td className="font-mono text-xs">{r.date}</td>
                    <td>{r.customer_name}{r.reversed ? " (reversed)" : ""}</td>
                    <td className="font-mono text-xs">{r.invoice_no || "—"}</td>
                    <td className="text-xs text-stone-500">{r.description || "—"}</td>
                    <td className="se-num" style={{ color: "#0f766e" }}>₹ {inr(r.amount)}</td>
                    <td className="text-right">
                        {!r.reversed && <Button size="sm" variant="outline" onClick={() => onReverse(r.id)} data-testid={`inv-reverse-${r.id}`}>Reverse</Button>}
                    </td>
                </tr>
            )}
        />
    );
}
function BillHistory({ rows, onReverse }) {
    return (
        <TableBase testid="history-bills" title="Fiat purchase bills"
            cols={[
                { key: "date", label: "Date" }, { key: "vend", label: "Vendor" },
                { key: "no", label: "Bill no." }, { key: "desc", label: "Desc." },
                { key: "amt", label: "Amount", right: true }, { key: "act", label: "" },
            ]}
            rows={rows}
            renderRow={(r) => (
                <tr key={r.id} data-testid={`bill-row-${r.id}`} className={r.reversed ? "opacity-50" : ""}>
                    <td className="font-mono text-xs">{r.date}</td>
                    <td>{r.vendor_name}{r.reversed ? " (reversed)" : ""}</td>
                    <td className="font-mono text-xs">{r.bill_no || "—"}</td>
                    <td className="text-xs text-stone-500">{r.description || "—"}</td>
                    <td className="se-num" style={{ color: "#9f1239" }}>₹ {inr(r.amount)}</td>
                    <td className="text-right">
                        {!r.reversed && <Button size="sm" variant="outline" onClick={() => onReverse(r.id)} data-testid={`bill-reverse-${r.id}`}>Reverse</Button>}
                    </td>
                </tr>
            )}
        />
    );
}
function ReceiptHistory({ rows, onReverse }) {
    return (
        <TableBase testid="history-receipts" title="Fiat receipts (money in)"
            cols={[
                { key: "date", label: "Date" }, { key: "cp", label: "Counterparty" },
                { key: "ref", label: "Ref" }, { key: "desc", label: "Desc." },
                { key: "amt", label: "Amount", right: true }, { key: "act", label: "" },
            ]}
            rows={rows}
            renderRow={(r) => (
                <tr key={r.id} data-testid={`rec-row-${r.id}`} className={r.reversed ? "opacity-50" : ""}>
                    <td className="font-mono text-xs">{r.date}</td>
                    <td>{r.counterparty_ledger_name}{r.reversed ? " (reversed)" : ""}</td>
                    <td className="font-mono text-xs">{r.ref || "—"}</td>
                    <td className="text-xs text-stone-500">{r.description || "—"}</td>
                    <td className="se-num" style={{ color: "#0f766e" }}>₹ {inr(r.amount)}</td>
                    <td className="text-right">
                        {!r.reversed && <Button size="sm" variant="outline" onClick={() => onReverse(r.id)} data-testid={`rec-reverse-${r.id}`}>Reverse</Button>}
                    </td>
                </tr>
            )}
        />
    );
}
function PaymentHistory({ rows, onReverse }) {
    return (
        <TableBase testid="history-payments" title="Fiat payments (money out)"
            cols={[
                { key: "date", label: "Date" }, { key: "cp", label: "To" },
                { key: "kind", label: "Kind" }, { key: "ref", label: "Ref" },
                { key: "amt", label: "Amount", right: true }, { key: "act", label: "" },
            ]}
            rows={rows}
            renderRow={(r) => (
                <tr key={r.id} data-testid={`pay-row-${r.id}`} className={r.reversed ? "opacity-50" : ""}>
                    <td className="font-mono text-xs">{r.date}</td>
                    <td>{r.counterparty_ledger_name}{r.reversed ? " (reversed)" : ""}</td>
                    <td><span className="se-chip" style={{ background: r.kind === "vendor" ? "#eff6ff" : "#fdf4ff", color: r.kind === "vendor" ? "#1e40af" : "#7c3aed" }}>{r.kind}</span></td>
                    <td className="font-mono text-xs">{r.ref || "—"}</td>
                    <td className="se-num" style={{ color: "#9f1239" }}>₹ {inr(r.amount)}</td>
                    <td className="text-right">
                        {!r.reversed && <Button size="sm" variant="outline" onClick={() => onReverse(r.id)} data-testid={`pay-reverse-${r.id}`}>Reverse</Button>}
                    </td>
                </tr>
            )}
        />
    );
}
